// Authenticated app layout — sidebar on left, main content on right.
// Wraps every route rendered under RequireAuth.
//
// On mobile (<md, 768px) the sidebar is an off-canvas drawer toggled by
// the hamburger button at top-left. On md+ the sidebar is permanently
// visible and the hamburger / backdrop are hidden.
//
// Mounts the global toast viewport and confirm-dialog host once here so
// any descendant can fire toast.success(...) / await confirm({...})
// without each component wiring up its own provider.

import { useEffect } from "react";
import { Outlet, useLocation } from "react-router-dom";
import { ConfirmHost } from "@/components/ui/ConfirmDialog";
import { ToastViewport } from "@/components/ui/Toast";
import { useDict } from "@/i18n/LocaleProvider";
import { useUiStore } from "@/stores/uiStore";
import { Sidebar } from "./Sidebar";

export function AppLayout() {
  const isSidebarOpen = useUiStore((s) => s.isSidebarOpen);
  const closeSidebar = useUiStore((s) => s.closeSidebar);
  const toggleSidebar = useUiStore((s) => s.toggleSidebar);
  const location = useLocation();
  const t = useDict().app;

  // Auto-close the drawer whenever the route changes — otherwise tapping
  // a nav item leaves the drawer open over the freshly-loaded page.
  useEffect(() => {
    closeSidebar();
  }, [location.pathname, closeSidebar]);

  // Esc closes the drawer on mobile.
  useEffect(() => {
    if (!isSidebarOpen) return;
    function onKey(e: KeyboardEvent) {
      if (e.key === "Escape") closeSidebar();
    }
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [isSidebarOpen, closeSidebar]);

  return (
    <div className="min-h-screen bg-bg">
      {/* Hamburger — only visible <md, hidden when drawer is open */}
      <button
        type="button"
        onClick={toggleSidebar}
        aria-label={isSidebarOpen ? t.closeMenu : t.openMenu}
        aria-expanded={isSidebarOpen}
        className={
          "md:hidden fixed top-3 left-3 z-30 w-11 h-11 flex items-center justify-center " +
          "bg-bg2 border border-stroke rounded text-content " +
          "active:bg-bg3 transition-colors " +
          (isSidebarOpen ? "opacity-0 pointer-events-none" : "opacity-100")
        }
      >
        <span className="text-[20px] leading-none">☰</span>
      </button>

      {/* Backdrop — only visible <md while drawer is open */}
      {isSidebarOpen && (
        <button
          type="button"
          aria-label={t.closeMenu}
          onClick={closeSidebar}
          className="md:hidden fixed inset-0 z-30 bg-black/60 animate-fade-in"
        />
      )}

      <Sidebar />

      <main className="ml-0 md:ml-[220px] p-4 md:p-6 pt-16 md:pt-6">
        <Outlet />
      </main>

      <ToastViewport />
      <ConfirmHost />
    </div>
  );
}
